import he from 'he';
import * as React from 'react';
import { Head, useRoutePath } from 'react-static';
import { useSiteRoot } from '../../common/hooks/useSiteRoot';
import { HeadMetaProps } from './HeadMeta.props';

function decodeSpecificHtmlEntities(str: string): string {
  return str
    .replace(/&#x27;/g, '’')
    .replace(/&quot;/g, '"')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/(^|[\s\(\[{<])"([^"]+)"(?=[\s\)\]}>,.!?;:]|$)/g, '$1“$2”')
    .replace(/(^|[\s\(\[{<])'([^']+)'(?=[\s\)\]}>,.!?;:]|$)/g, '$1‘$2’')
    .replace(/(\w)'(\w)/g, '$1’$2');
}

function decodeHtmlEntities(html: string): string {
  if (!html) return '';

  try {
    // If running in browser — use DOMParser for better performance
    if (typeof window !== 'undefined' && typeof DOMParser !== 'undefined') {
      const parser = new DOMParser();
      const decoded = parser.parseFromString(html, 'text/html').documentElement.textContent;
      return decodeSpecificHtmlEntities(decoded || '') || decodeSpecificHtmlEntities(html);
    }
  } catch {
    // ignore and fall back
  }

  // Fallback for SSR/Node or parsing errors
  return he.decode(html);
}

export const HeadMeta = ({ locale, description }: HeadMetaProps) => {
  const currentPath = useRoutePath('');
  const siteRoot = useSiteRoot();
  const fullDescription: string = decodeHtmlEntities(String(description || ''));

  const canonicalUrl = `${siteRoot}/${currentPath}`.replace(/\/+$/, '');
  const shareImageUrl = `${siteRoot}/assets/logo.png`;

  return (
    <>
      <Head>
        {/* tslint:disable-next-line:react-a11y-lang */}
        {locale && <html lang={locale} />}
        <meta charSet='UTF-8' />
        <meta httpEquiv='X-UA-Compatible' content='IE=edge' />
        <meta name='viewport' content='width=device-width, initial-scale=1, shrink-to-fit=no' />
        {fullDescription && <meta name='description' content={fullDescription} />}
        <link rel='icon' type='image/x-icon' href={'/assets/favicon.ico'} />
        <link rel='icon' type='image/png' sizes='16x16' href={'/assets/favicon-16x16.png'} />
        <link rel='icon' type='image/png' sizes='32x32' href={'/assets/favicon-32x32.png'} />
        <link rel='apple-touch-icon' sizes='180x180' href={'/assets/apple-touch-icon.png'} />
        <link rel='manifest' href={'/assets/manifest.json'} />
        <link rel='canonical' href={canonicalUrl} />

        {/* Open Graph / Facebook */}
        <meta property='og:type' content='website' />
        <meta property='og:url' content={canonicalUrl} />
        <meta property='og:image' content={shareImageUrl} />
        <meta property='og:image:width' content='1200' />
        <meta property='og:image:height' content='600' />
        {fullDescription && <meta property='og:description' content={fullDescription} />}

        {/* Twitter */}
        <meta name='twitter:card' content='summary_large_image' />
        <meta name='twitter:image' content={shareImageUrl} />
      </Head>
    </>
  );
};
